import { FirebaseError, initializeApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";
import {
  connectAuthEmulator,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  getAuth,
  GoogleAuthProvider,
  initializeRecaptchaConfig,
  initializeAuth,
  OAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth";
import { getFirestore, initializeFirestore } from "firebase/firestore";
import { connectFirestoreEmulator } from "firebase/firestore";
import { getStorage, ref, deleteObject } from "firebase/storage";
import { getAnalytics, isSupported, logEvent, Analytics } from "firebase/analytics";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { getMessaging, isSupported as isMessagingSupported } from "firebase/messaging";
import { authProbe } from "./lib/authProbe";
import { logger } from "./lib/logger";
import { assertValidStoragePath } from "./lib/storagePathValidation";

const useLocalPreviewFirebaseConfig = import.meta.env.MODE !== "production" && !import.meta.env.VITE_FIREBASE_API_KEY;

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || (useLocalPreviewFirebaseConfig ? "local-preview-api-key" : undefined),
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || (useLocalPreviewFirebaseConfig ? "localhost" : undefined),
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || (useLocalPreviewFirebaseConfig ? "local-preview" : undefined),
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || (useLocalPreviewFirebaseConfig ? "local-preview.appspot.com" : undefined),
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || (useLocalPreviewFirebaseConfig ? "000000000000" : undefined),
  appId: import.meta.env.VITE_FIREBASE_APP_ID || (useLocalPreviewFirebaseConfig ? "1:000000000000:web:localpreview" : undefined),
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

export const app = initializeApp(firebaseConfig);

// C1: Named database ID hardcoded — iamvault-main is the named Firestore DB (not free-tier default)
// IMPORTANT: Do NOT revert to "(default)" — that is the free-tier DB with 50K read/day hard cap.
initializeFirestore(app, {
  experimentalAutoDetectLongPolling: true,
}, "iamvault-main");
export const db = getFirestore(app, "iamvault-main");
export const auth = typeof window === "undefined"
  ? getAuth(app)
  : initializeAuth(app, {
      persistence: browserLocalPersistence,
      popupRedirectResolver: browserPopupRedirectResolver,
    });
export const authPersistenceReady = typeof window === "undefined"
  ? Promise.resolve<"server" | "local">("server")
  : (async () => {
      const authWithStateReady = auth as typeof auth & { authStateReady?: () => Promise<void> };

      if (typeof authWithStateReady.authStateReady === "function") {
        try {
          await authWithStateReady.authStateReady();
          authProbe("auth:state-ready", { authCurrentUserPresent: Boolean(auth.currentUser?.uid) });
        } catch (error) {
          authProbe("auth:state-ready:error", { message: error instanceof Error ? error.message : String(error) });
          logger.warn("Firebase Auth state readiness check failed", { error });
        }
      }

      try {
        await initializeRecaptchaConfig(auth);
        authProbe("auth:recaptcha-config:ready", { authCurrentUserPresent: Boolean(auth.currentUser?.uid) });
      } catch (error) {
        authProbe("auth:recaptcha-config:error", { message: error instanceof Error ? error.message : String(error) });
        logger.warn("Firebase Auth reCAPTCHA config preload failed", { error });
      }

      return "local";
    })();
authProbe("firebase:init", {
  appName: app.name,
  authAppName: auth.app.name,
  authCurrentUserPresent: Boolean(auth.currentUser?.uid),
  hostname: typeof window === "undefined" ? "server" : window.location.hostname,
});
export const storage = getStorage(app);
export const functions = getFunctions(app, "europe-west2");
export const googleProvider = new GoogleAuthProvider();

// FCM — lazy init (only in browsers that support it)
export const getMessagingInstance = async () => {
  try {
    const supported = await isMessagingSupported();
    if (supported) return getMessaging(app);
  } catch {
    // Safari / non-supporting browsers
  }
  return null;
};

let analyticsInstance: Analytics | null = null;

export const initAnalytics = async (): Promise<Analytics | null> => {
  if (analyticsInstance || typeof window === "undefined" || !firebaseConfig.measurementId) {
    return analyticsInstance;
  }

  try {
    const supported = await isSupported();
    if (supported) {
      analyticsInstance = getAnalytics(app);
    }
  } catch (error) {
    logger.warn("Firebase Analytics not supported in this environment.", { error });
  }

  return analyticsInstance;
};

export const trackEvent = async (eventName: string, params?: Record<string, unknown>) => {
  try {
    const analytics = await initAnalytics();
    if (analytics) {
      logEvent(analytics, eventName, params);
    }
  } catch (error) {
    logger.warn("Unable to record analytics event", { eventName, error });
  }
};

function isPopupFallbackError(error: unknown): boolean {
  return error instanceof FirebaseError && (error.code === "auth/popup-blocked" || error.code === "auth/cancelled-popup-request");
}

googleProvider.setCustomParameters({ prompt: "select_account" });

export const microsoftProvider = new OAuthProvider("microsoft.com");
microsoftProvider.setCustomParameters({ prompt: "select_account" });

export const signInWithGoogle = async () => {
  try {
    return await signInWithPopup(auth, googleProvider);
  } catch (error: unknown) {
    if (isPopupFallbackError(error)) {
      logger.warn("Popup blocked, falling back to redirect flow", { error });
      return signInWithRedirect(auth, googleProvider);
    }
    throw error;
  }
};
export const logOut = () => signOut(auth);

export const signInWithMicrosoft = async () => {
  try {
    return await signInWithPopup(auth, microsoftProvider);
  } catch (error: unknown) {
    if (isPopupFallbackError(error)) {
      logger.warn("Microsoft popup blocked, falling back to redirect flow", { error });
      return signInWithRedirect(auth, microsoftProvider);
    }
    throw error;
  }
};

export const signInWithEmail = (email: string, password: string) =>
  signInWithEmailAndPassword(auth, email, password);

export const sendPasswordReset = (email: string) =>
  sendPasswordResetEmail(auth, email);

export const deleteFile = async (storagePath: string) => {
  if (!storagePath || storagePath === "not_saved" || storagePath === "base64_fallback") return;
  try {
    const normalizedPath = assertValidStoragePath(null, storagePath, "deleteFile");
    const storageRef = ref(storage, normalizedPath);
    await deleteObject(storageRef);
  } catch (error) {
    logger.error("Error deleting file from storage:", { error });
    // Don't throw, we want to continue deleting the firestore record even if storage fails
  }
};

export enum OperationType {
  CREATE = "create",
  UPDATE = "update",
  DELETE = "delete",
  LIST = "list",
  GET = "get",
  WRITE = "write",
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId: string | undefined;
    email: string | null | undefined;
    emailVerified: boolean | undefined;
    isAnonymous: boolean | undefined;
    tenantId: string | null | undefined;
    providerInfo: {
      providerId: string;
      displayName: string | null;
      email: string | null;
      photoUrl: string | null;
    }[];
  };
}

export function handleFirestoreError(
  error: unknown,
  operationType: OperationType,
  path: string | null
) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo:
        auth.currentUser?.providerData.map((provider) => ({
          providerId: provider.providerId,
          displayName: provider.displayName,
          email: provider.email,
          photoUrl: provider.photoURL,
        })) || [],
    },
    operationType,
    path,
  };
  logger.error("Firestore Error: ", { info: errInfo });
  throw new Error(JSON.stringify(errInfo));
}

logger.debug("Firebase initialized.");

const recaptchaKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
const isLocalHost =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
const isHostedProductionDomain =
  typeof window !== "undefined" &&
  (window.location.hostname === "iamvault.app" || window.location.hostname === "www.iamvault.app" || window.location.hostname === "control.iamvault.app");

// When the app falls back to the synthetic local-preview Firebase config,
// it must also switch to the emulator suite. Otherwise localhost ends up
// pointing at a non-existent hosted backend and email/password sign-in fails
// before any RBAC/bootstrap logic is exercised.
const useFunctionsEmulator =
  isLocalHost &&
  (
    import.meta.env.VITE_USE_FUNCTIONS_EMULATOR === "1" ||
    import.meta.env.VITE_USE_FUNCTIONS_EMULATOR === "true" ||
    useLocalPreviewFirebaseConfig
  );

if (useFunctionsEmulator) {
  connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
  connectFirestoreEmulator(db, "127.0.0.1", 8080);
  connectFunctionsEmulator(functions, "127.0.0.1", 5001);
}

if (isLocalHost && typeof self !== "undefined") {
  const debugToken = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN;
  // @ts-ignore — Firebase App Check picks up self.FIREBASE_APPCHECK_DEBUG_TOKEN automatically
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken || true;
}

if (typeof window !== "undefined" && recaptchaKey && !isLocalHost && !isHostedProductionDomain) {
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(recaptchaKey),
      isTokenAutoRefreshEnabled: true,
    });
    authProbe("app-check:init", { appName: app.name, mode: "production", recaptchaConfigured: true });
    logger.info("Firebase App Check initialized.", { mode: isLocalHost ? "debug" : "production" });
  } catch (error) {
    authProbe("app-check:init-error", { message: error instanceof Error ? error.message : String(error) });
    logger.error("App Check initialization failed", { error });
  }
} else if (typeof window !== "undefined" && isLocalHost) {
  authProbe("app-check:skipped", { reason: "localhost" });
  logger.info("App Check skipped on localhost.");
} else if (typeof window !== "undefined" && isHostedProductionDomain) {
  authProbe("app-check:skipped", { reason: "hosted-production-domain-bypass" });
  logger.warn("App Check temporarily bypassed on hosted production domain while reCAPTCHA Enterprise validation is unhealthy.");
} else if (typeof window !== "undefined") {
  authProbe("app-check:skipped", { reason: "missing-recaptcha-site-key" });
  logger.warn("App Check not initialized", {
    reason: "missing VITE_RECAPTCHA_SITE_KEY",
  });
}
